/* Kook's classic BookThatApp form adapter. Cart deposits are handled separately. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (root.KookBooking) root.KookBooking.scan();
  else {
    root.KookBooking = api.start(root);
  }
})(typeof window === 'undefined' ? this : window, function () {
  'use strict';

  function usesNativeWidget(config, productId) {
    const product = config && config.product;
    const settings = config && config.settings;
    const types = ['activity', 'appt', 'course', 'class', 'events_list', 'rental', 'datepicker'];
    return Boolean(product && String(product.external_id) === String(productId) && product.widget_id &&
      settings && Array.isArray(settings.widget_enabled_list) &&
      types.some(type => settings.widget_enabled_list.includes(type)));
  }

  function start(win) {
    const doc = win.document;
    const controllers = new WeakMap();

    function attach(root) {
      if (controllers.has(root)) return;
      const form = root.closest('form');
      const template = root.querySelector('[data-kook-booking-fields]');
      const status = root.querySelector('[data-kook-booking-status]');
      if (!form || !template || !status) return;
      controllers.set(root, true);
      // Shopify's variant control is named "id", which can shadow form.id.
      const formId = form.getAttribute('id');
      const controlScope = form.closest('.product-section') || form.closest('.shopify-section') || doc;

      // An enabled app embed alone does not mean a product widget is configured.
      if (usesNativeWidget(win.BtaConfig, root.dataset.productId)) {
        controlScope.querySelectorAll('[data-kook-booking-pending]').forEach(block => {
          if (block.getAttribute('data-kook-booking-theme-quantity') === formId) {
            block.removeAttribute('data-kook-booking-pending');
          }
        });
        root.hidden = true;
        return;
      }

      let product;
      try {
        product = JSON.parse(root.querySelector('[data-kook-booking-product]').textContent);
      } catch (_) {
        status.textContent = 'The booking calendar could not load. Please refresh this page.';
        form.addEventListener('submit', event => {
          event.preventDefault();
          event.stopImmediatePropagation();
          status.focus();
        }, true);
        return;
      }
      root.insertBefore(template.content.cloneNode(true), template);
      const fields = root.querySelector('.booking-form');
      const from = fields.querySelector('.bta-dp-start');
      const to = fields.querySelector('.bta-dp-finish');
      const pickup = fields.querySelector('[name="properties[start-time]"]');
      const dropoff = fields.querySelector('[name="properties[finish-time]"]');
      const units = fields.querySelector('[name="properties[Units]"]');
      const unitsControl = fields.querySelector('[data-kook-booking-units]');
      const quantity = fields.querySelector('[data-kook-booking-quantity]');
      const days = fields.querySelector('[data-kook-booking-days]');
      const total = fields.querySelector('[data-kook-booking-total]');
      const returnMessage = fields.querySelector('[data-kook-booking-return]');
      let booking = null;
      let subscribed = false;
      let attempts = 0;
      let timer = null;
      let queued = false;

      // BTA owns the only Units field and the hidden charged-day quantity.
      // Suppress theme controls inside or associated with this form, including
      // quantity blocks reinserted by the theme after initialization.
      function suppressThemeQuantity() {
        controlScope.querySelectorAll('[name="quantity"]').forEach(input => {
          if (root.contains(input)) return;
          const belongsToForm = input.form === form || input.closest('form') === form ||
            input.getAttribute('form') === formId;
          if (!belongsToForm) return;
          if (!input.disabled) input.disabled = true;
          const block = input.closest('[data-kook-booking-theme-quantity]') ||
            input.closest('.product__quantity') || input.closest('.product-block');
          if (block && !block.contains(root)) {
            if (!block.hidden) block.hidden = true;
            if (!block.hasAttribute('data-kook-booking-suppressed')) block.setAttribute('data-kook-booking-suppressed', '');
            if (block.hasAttribute('data-kook-booking-pending')) block.removeAttribute('data-kook-booking-pending');
          } else {
            if (!input.hidden) input.hidden = true;
            if (!input.hasAttribute('data-kook-booking-suppressed')) input.setAttribute('data-kook-booking-suppressed', '');
          }
        });
      }
      suppressThemeQuantity();
      let quantityObserver = null;
      if (typeof win.MutationObserver === 'function') {
        quantityObserver = new win.MutationObserver(() => {
          if (root.isConnected) {
            suppressThemeQuantity();
            syncUnitButtons();
          }
          else quantityObserver.disconnect();
        });
        quantityObserver.observe(controlScope, { childList: true, subtree: true });
        quantityObserver.observe(units, { attributes: true, attributeFilter: ['disabled', 'readonly', 'min', 'max', 'step'] });
        quantityObserver.observe(from, { attributes: true, attributeFilter: ['disabled'] });
        quantityObserver.observe(form, { attributes: true, attributeFilter: ['class'] });
      }

      function ready() {
        return booking && form.classList.contains('bta-active') && !from.disabled;
      }

      function selectedVariant() {
        const selected = form.querySelector('[name="id"]');
        return selected && product.variants.find(variant => String(variant.id) === String(selected.value));
      }

      // Step the provider's actual Units input, never the charged-day quantity.
      // A detached copy lets the browser apply its own min/max/step rules when
      // deciding whether a button can move, without changing provider state.
      function steppedUnits(direction) {
        if (typeof units.stepUp !== 'function' || !units.value.trim() || !Number.isSafeInteger(Number(units.value))) return null;
        try {
          const candidate = units.cloneNode();
          candidate.value = units.value;
          candidate.stepUp(direction);
          const value = Number(candidate.value);
          if (!Number.isSafeInteger(value) || value < 1 || value === Number(units.value) ||
              (candidate.validity && !candidate.validity.valid)) return null;
          return value;
        } catch (_) {
          return null;
        }
      }

      function canChangeUnits() {
        const variant = selectedVariant();
        return ready() && variant && variant.available && !units.disabled && !units.readOnly;
      }

      function syncUnitButtons() {
        if (!unitsControl) return;
        const enabled = canChangeUnits();
        unitsControl.querySelectorAll('[data-kook-booking-units-step]').forEach(button => {
          const direction = Number(button.getAttribute('data-kook-booking-units-step'));
          const disabled = !enabled || steppedUnits(direction) === null;
          if (button.disabled !== disabled) button.disabled = disabled;
        });
      }

      if (unitsControl) unitsControl.addEventListener('click', event => {
        const button = event.target.closest('[data-kook-booking-units-step]');
        if (!button || !unitsControl.contains(button)) return;
        event.preventDefault();
        const direction = Number(button.getAttribute('data-kook-booking-units-step'));
        if (button.disabled || !canChangeUnits() || (direction !== -1 && direction !== 1) || steppedUnits(direction) === null) return;
        try { units.stepUp(direction); } catch (_) { return; }
        units.dispatchEvent(new win.Event('input', { bubbles: true }));
        units.dispatchEvent(new win.Event('change', { bubbles: true }));
        scheduleUpdate();
        syncUnitButtons();
        if (button.disabled) units.focus({ preventScroll: true });
      });
      units.addEventListener('input', syncUnitButtons);
      units.addEventListener('change', syncUnitButtons);
      syncUnitButtons();

      function mirrorReturnTime() {
        const option = Array.from(dropoff.options).find(option => option.value === pickup.value && !option.disabled);
        if (pickup.value && option) dropoff.value = pickup.value;
        return Boolean(pickup.value && option);
      }

      function needsPickupTime() {
        const jq = win.jQ || win.jQuery;
        return Boolean(jq && jq(pickup).is(':visible'));
      }

      function update() {
        if (!booking) {
          syncUnitButtons();
          return;
        }
        try {
          const matchedTime = mirrorReturnTime();
          // BTA calculates charged days before clamping Units to capacity. Its
          // clamp event does not bubble, so recalculate once if it changed Units.
          const requestedUnits = units.value;
          booking.updateQuantity();
          if (units.value !== requestedUnits) booking.updateQuantity();
          const count = booking.rangeCount();
          const variant = selectedVariant();
          const chargedQuantity = Number(quantity.value);
          const start = booking.getStartDateTime();
          const finish = booking.getFinishDateTime();
          days.textContent = start && finish && count > 0 ? String(count) : '—';
          total.textContent = '—';
          if (start && finish && count > 0 && variant && Number.isFinite(chargedQuantity) && chargedQuantity > 0) {
            total.textContent = new win.Intl.NumberFormat(product.locale || 'en', {
              style: 'currency', currency: product.currency || 'AED'
            }).format(variant.price * chargedQuantity / 100);
          }
          if (start && finish && matchedTime) {
            const selectedTime = pickup.options[pickup.selectedIndex];
            returnMessage.textContent = 'Return: ' + selectedTime.textContent + ' on ' + to.value + '.';
          } else if (start && finish && !needsPickupTime()) {
            returnMessage.textContent = 'Return: ' + to.value + '.';
          } else {
            returnMessage.textContent = 'Choose dates and pickup time.';
          }
          if (ready()) status.hidden = true;
        } catch (_) {
          // Availability data can still be loading when the app emits a change.
        } finally {
          syncUnitButtons();
        }
      }

      function scheduleUpdate() {
        if (queued) return;
        queued = true;
        win.setTimeout(() => { queued = false; update(); }, 0);
      }

      function prevent(event, message) {
        event.preventDefault();
        event.stopImmediatePropagation();
        status.hidden = false;
        status.textContent = message;
        status.focus();
      }

      form.addEventListener('submit', event => {
        suppressThemeQuantity();
        if (!ready()) {
          prevent(event, 'Please wait for the booking calendar to load, or refresh this page.');
          return;
        }
        const variant = selectedVariant();
        const selectedUnits = Number(units.value);
        if (!variant || !variant.available || !Number.isSafeInteger(selectedUnits) || selectedUnits < 1) {
          prevent(event, 'Please choose an available product and a valid quantity.');
          return;
        }
        update();
        if (needsPickupTime() && !mirrorReturnTime()) {
          prevent(event, 'Please choose available rental dates and a pickup time.');
          return;
        }
        booking.preventDefaultHandler(event);
      }, true);

      form.addEventListener('change', scheduleUpdate);
      form.addEventListener('bta.datetimeChange', event => {
        booking = event.detail && event.detail.form || booking;
        scheduleUpdate();
      });
      form.addEventListener('bta.formLoaded', event => {
        booking = event.detail && event.detail.form || booking;
        scheduleUpdate();
      });
      const section = form.closest('.product-section');
      if (section) section.addEventListener('variantChange', event => {
        if (!booking || !event.detail) return;
        suppressThemeQuantity();
        const variant = event.detail.variant;
        booking.setSelectedVariantId(variant ? variant.id : null);
        if (variant) booking.triggerDatetimeChange();
        scheduleUpdate();
      });

      function connect() {
        if (!root.isConnected) {
          win.clearTimeout(timer);
          if (quantityObserver) quantityObserver.disconnect();
          return;
        }
        attempts += 1;
        const jq = win.jQ || win.jQuery;
        const bta = win.bta;
        // The provider loads its dependencies asynchronously. A jQuery global
        // alone does not mean the datepicker plugin has finished registering.
        if (jq && jq.fn && typeof jq.fn.datepicker === 'function' &&
            bta && bta.initialized && typeof bta.BookingForm === 'function') {
          const wrappedForm = jq(form);
          if (!subscribed) {
            subscribed = true;
            wrappedForm.on('bta.dataLoaded.kookBooking bta.timeslotsLoaded.kookBooking', function (event, api) {
              booking = api || booking;
              scheduleUpdate();
            });
          }
          booking = wrappedForm.data('bta.bookingForm');
          if (!booking) {
            // Preserve the app object; never overwrite its functions/settings.
            bta.productId = Number(root.dataset.productId);
            try {
              booking = new bta.BookingForm(wrappedForm);
            } catch (_) {
              // The provider caches its instance before initializing it. Do not
              // construct a second instance and risk duplicate app handlers.
              status.hidden = false;
              status.textContent = 'The booking calendar could not load. Please refresh this page.';
              return;
            }
          }
          suppressThemeQuantity();
          update();
          if (ready()) return;
        }
        if (attempts < 150) timer = win.setTimeout(connect, 100);
        else {
          status.hidden = false;
          status.textContent = 'The booking calendar could not load. Please refresh this page.';
        }
      }
      connect();
    }

    function scan(scope) {
      (scope || doc).querySelectorAll('[data-kook-booking]').forEach(attach);
    }
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => scan(), { once: true });
    else scan();
    doc.addEventListener('shopify:section:load', event => scan(event.target));
    return { scan: scan };
  }

  return { start: start, usesNativeWidget: usesNativeWidget };
});
